import { useMemo, useState, useCallback } from 'react';
import {
    collectDiffEntries,
    buildChildrenMap,
    getRootUuids,
    toggleSelection,
    selectAllUuids,
    countSelectionByType,
    changeTypeLabel,
    changeTypeIcon,
} from './diffSelectorUtils';
import styles from '../GirderConnection.module.css';

function DiffTreeNode({
    uuid,
    byUuid,
    childrenByParent,
    selectedSet,
    onToggle,
    depth,
    expandedSet,
    onExpandToggle,
}) {
    const entry = byUuid[uuid];
    if (!entry) return null;

    const children = childrenByParent[uuid] || [];
    const hasChildren = children.length > 0;
    const isExpanded = expandedSet.has(uuid);
    const isRemove = entry.changeType === 'remove';

    return (
        <div className={styles.diffTreeNode} style={{ marginLeft: depth * 16 }}>
            <div className={`${styles.diffTreeRow} ${isRemove ? styles.diffTreeRowRemove : ''}`}>
                {hasChildren ? (
                    <button
                        type="button"
                        className={styles.diffTreeExpand}
                        onClick={() => onExpandToggle(uuid)}
                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                    >
                        {isExpanded ? '▼' : '▶'}
                    </button>
                ) : (
                    <span className={styles.diffTreeExpandPlaceholder} />
                )}
                <input
                    type="checkbox"
                    checked={selectedSet.has(uuid)}
                    onChange={(e) => onToggle(uuid, e.target.checked)}
                />
                <span className={styles.diffTreeIcon}>{changeTypeIcon(entry.type)}</span>
                <span className={styles.diffTreeName}>{entry.displayName}</span>
                <span className={`${styles.diffChangeBadge} ${styles[`diffChangeBadge_${entry.changeType}`]}`}>
                    {changeTypeLabel(entry.changeType)}
                </span>
            </div>
            {hasChildren && isExpanded && children.map((childUuid) => (
                <DiffTreeNode
                    key={childUuid}
                    uuid={childUuid}
                    byUuid={byUuid}
                    childrenByParent={childrenByParent}
                    selectedSet={selectedSet}
                    onToggle={onToggle}
                    depth={depth + 1}
                    expandedSet={expandedSet}
                    onExpandToggle={onExpandToggle}
                />
            ))}
        </div>
    );
}

function NodeDiffGroup({
    nodeDiff,
    nodeName,
    selectedSet,
    onSelectionChange,
    pluginPreview,
}) {
    const { entries, byUuid } = useMemo(() => collectDiffEntries(nodeDiff), [nodeDiff]);
    const childrenByParent = useMemo(() => buildChildrenMap(entries), [entries]);
    const roots = useMemo(() => getRootUuids(entries, byUuid), [entries, byUuid]);
    const [expandedSet, setExpandedSet] = useState(() => new Set(roots));

    const onToggle = useCallback((uuid, checked) => {
        onSelectionChange(toggleSelection(uuid, selectedSet, childrenByParent, checked));
    }, [selectedSet, childrenByParent, onSelectionChange]);

    const onExpandToggle = useCallback((uuid) => {
        setExpandedSet((prev) => {
            const next = new Set(prev);
            if (next.has(uuid)) next.delete(uuid);
            else next.add(uuid);
            return next;
        });
    }, []);

    const bdsaSummary = pluginPreview?.bdsa?.summary;

    if (entries.length === 0) return null;

    return (
        <div className={styles.diffNodeGroup}>
            <div className={styles.diffNodeGroupTitle}>Node: {nodeName}</div>
            {roots.map((rootUuid) => (
                <DiffTreeNode
                    key={rootUuid}
                    uuid={rootUuid}
                    byUuid={byUuid}
                    childrenByParent={childrenByParent}
                    selectedSet={selectedSet}
                    onToggle={onToggle}
                    depth={0}
                    expandedSet={expandedSet}
                    onExpandToggle={onExpandToggle}
                />
            ))}
            {bdsaSummary && (
                <div className={styles.diffBdsaHint}>{bdsaSummary}</div>
            )}
        </div>
    );
}

export default function DiffSelector({
    diffResult,
    statistics,
    pluginPreview,
    selectedUuids,
    onSelectionChange,
}) {
    const selectedSet = useMemo(() => new Set(selectedUuids), [selectedUuids]);
    const byNode = statistics?.byNode || {};
    const diffs = diffResult?.diffs || [];

    const handleSelectAll = () => {
        const all = new Set();
        for (const nodeDiff of diffs) {
            const { entries } = collectDiffEntries(nodeDiff);
            for (const uuid of selectAllUuids(entries)) all.add(uuid);
        }
        onSelectionChange(all);
    };

    const handleSelectNone = () => onSelectionChange(new Set());

    let totalFolders = 0;
    let totalItems = 0;
    for (const nodeDiff of diffs) {
        const { entries, byUuid } = collectDiffEntries(nodeDiff);
        const counts = countSelectionByType(selectedSet, byUuid);
        totalFolders += counts.folders;
        totalItems += counts.items;
    }

    return (
        <div className={styles.diffSelector}>
            <div className={styles.diffSelectorToolbar}>
                <button type="button" className={styles.diffToolbarButton} onClick={handleSelectAll}>
                    Select all
                </button>
                <button type="button" className={styles.diffToolbarButton} onClick={handleSelectNone}>
                    Select none
                </button>
            </div>
            {diffs.map((nodeDiff) => {
                const nodeUuid = nodeDiff.girderNetworkUuid;
                const nodeName = byNode[nodeUuid]?.nodeName || nodeUuid;
                return (
                    <NodeDiffGroup
                        key={nodeUuid}
                        nodeDiff={nodeDiff}
                        nodeName={nodeName}
                        selectedSet={selectedSet}
                        onSelectionChange={onSelectionChange}
                        pluginPreview={pluginPreview}
                    />
                );
            })}
            <div className={styles.diffSelectorFooter}>
                {totalFolders} folder{totalFolders !== 1 ? 's' : ''}, {totalItems} item{totalItems !== 1 ? 's' : ''} selected
            </div>
        </div>
    );
}
